import Image from 'next/image';
import React from 'react';

import { useAppContext } from '../../hooks/use-app-context';
import { useWindowSize } from '../../hooks/use-window-size';

/**
 * ルーム切り替えボタンコンポーネントのプロパティ
 */
interface RoomChangeButtonProps {
  /** ボタンの方向（前へ/次へ） */
  direction: 'prev' | 'next';
  /** クリック時のコールバック */
  onClick: () => void;
  /** ボタンを無効化するかどうか */
  disabled?: boolean;
}

/**
 * ルーム切り替えボタンコンポーネント
 *
 * 画面の左右に配置され、前後のルームに切り替えるためのボタン
 */
function RoomChangeButton({ direction, onClick, disabled = false }: RoomChangeButtonProps) {
  const { width } = useWindowSize();

  // 画面サイズに応じてボタンのサイズと位置を調整
  const isSmallScreen = width < 768;
  const isMediumScreen = width >= 768 && width < 1024;

  // 動的なスタイル計算
  const buttonPosition = isSmallScreen ? '20px' : isMediumScreen ? '40px' : '88px';
  const buttonSize = isSmallScreen ? 36 : isMediumScreen ? 42 : 48;

  return (
    <div
      style={{
        position: 'fixed',
        ...(direction === 'prev' ? { left: buttonPosition } : { right: buttonPosition }),
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1001,
        cursor: disabled ? 'not-allowed' : 'pointer',
        pointerEvents: 'auto',
        opacity: disabled ? 0.5 : 1
      }}
      onClick={onClick}
    >
      <Image
        alt={direction === 'prev' ? '前のルーム' : '次のルーム'}
        height={buttonSize}
        src={direction === 'prev' ? '/icon_arrow_back_ios_48p.png' : '/icon_arrow_forward_ios_48p.png'}
        width={buttonSize}
      />
    </div>
  );
}

/**
 * ルームナビゲーターコンポーネントのプロパティ
 */
export interface RoomNavigatorProps {
  /** コンポーネントの位置調整用スタイル */
  style?: React.CSSProperties;
  /** ナビゲーターを無効化するかどうか */
  disabled?: boolean;
}

/**
 * ルームナビゲーターコンポーネント
 *
 * 左右の矢印ボタンでルームを切り替えるコンポーネント
 */
export function RoomNavigator({ disabled = false }: RoomNavigatorProps): React.ReactElement {
  const { state, switchRoom } = useAppContext();
  const { rooms, currentRoomId } = state;

  // 現在のルームのインデックスを取得
  const currentIndex = rooms.findIndex((room) => room.id === currentRoomId);

  // 前のルームに切り替え
  const goToPrevRoom = () => {
    if (disabled) return;

    if (currentIndex > 0) {
      const prevRoom = rooms[currentIndex - 1];
      if (prevRoom) {
        switchRoom(prevRoom.id);
      }
    }
  };

  // 次のルームに切り替え
  const goToNextRoom = () => {
    if (disabled) return;

    if (currentIndex < rooms.length - 1) {
      const nextRoom = rooms[currentIndex + 1];
      if (nextRoom) {
        switchRoom(nextRoom.id);
      }
    }
  };

  // 前のルームがあるかどうか
  const hasPrevRoom = currentIndex > 0;

  // 次のルームがあるかどうか
  const hasNextRoom = currentIndex < rooms.length - 1;

  return (
    <>
      {/* 前のルームがある場合のみ表示 */}
      {hasPrevRoom && <RoomChangeButton direction='prev' disabled={disabled} onClick={goToPrevRoom} />}

      {/* 次のルームがある場合のみ表示 */}
      {hasNextRoom && <RoomChangeButton direction='next' disabled={disabled} onClick={goToNextRoom} />}
    </>
  );
}
